import { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { BarChart3, Key, TrendingUp, Hash, Link2, ChevronDown, ChevronRight } from 'lucide-react';
import { NODE_COLORS } from '../../utils/constants';
import useFlowStore from '../../stores/useFlowStore';
import EntityNodeToolbar from './EntityNodeToolbar';
import FkRelationshipAlias from './FkRelationshipAlias';

const wrapText = (text, maxLength = 60) => {
  if (!text || text.length <= maxLength) return text;
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';
  for (const word of words) {
    if ((currentLine + word).length <= maxLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines.join('\n');
};

const FactNode = ({ data, selected, id }) => {
  const { name, schema, fields = [], color, tags = [], domains = [] } = data;
  const collapsed = !!data.collapsed;
  const showFieldDescriptions = useFlowStore((state) => state.showFieldDescriptions);
  const edges = useFlowStore((state) => state.edges);
  const nodes = useFlowStore((state) => state.nodes);
  const toggleNodeCollapsed = useFlowStore((state) => state.toggleNodeCollapsed);
  const highlightedNodes = useFlowStore((state) => state.highlightedNodes);
  const isHighlighted = highlightedNodes.includes(id);

  const nodeColor = (tags.length > 0 && tags[0].color) || color || NODE_COLORS.fact;

  const incomingEdges = edges.filter(edge => edge.target === id);
  const outgoingEdges = edges.filter(edge => edge.source === id);
  const targetHandleCount = collapsed ? 1 : Math.max(1, incomingEdges.length);
  const sourceHandleCount = collapsed ? 1 : Math.max(1, outgoingEdges.length);

  const generateHandlePositions = (count) => {
    if (count === 1) return [50];
    const positions = [];
    const spacing = 80 / (count + 1);
    const startOffset = 10;
    for (let i = 0; i < count; i++) {
      positions.push(startOffset + spacing * (i + 1));
    }
    return positions;
  };

  const targetPositions = generateHandlePositions(targetHandleCount);
  const sourcePositions = generateHandlePositions(sourceHandleCount);

  const generateVirtualFKFields = () => {
    return incomingEdges
      .filter(edge => edge.data?.cardinality !== 'N:N')
      .map(edge => {
        const sourceNode = nodes.find(n => n.id === edge.source);
        const sourceName = sourceNode?.data?.schema
          ? `${sourceNode.data.schema}.${sourceNode.data.name}`
          : (sourceNode?.data?.name || 'unknown');
        const fkAlias = (edge.data?.fkAlias && String(edge.data.fkAlias).trim()) || '';
        return { sourceName, fkAlias, isForeignKey: true, isVirtual: true, edgeId: edge.id };
      });
  };

  const virtualFKFields = collapsed ? [] : generateVirtualFKFields();

  const measures = fields.filter(f =>
    f.type?.includes('DECIMAL') || f.type?.includes('FLOAT') || f.type?.includes('NUMERIC')
  );
  const dimensions = fields.filter(f => !measures.includes(f));

  const partitioning = data.partitioning;
  const isPartitioned = partitioning && partitioning.type && partitioning.type !== 'none';
  const partitionColumns = isPartitioned
    ? (partitioning.type === 'hash'
        ? (partitioning.columns || [])
        : (partitioning.column ? [partitioning.column] : (partitioning.columns || [])))
    : [];

  const displayName = schema ? `${schema}.${name}` : name;

  return (
    <>
    <EntityNodeToolbar id={id} selected={selected} />
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border-2 transition-all ${
        selected
          ? 'border-amber-500 shadow-xl'
          : isHighlighted
            ? 'border-blue-400 shadow-md ring-2 ring-blue-200 dark:ring-blue-800'
            : 'border-gray-300 dark:border-gray-600'
      }`}
      style={{ minWidth: '250px' }}
    >
      {/* Header */}
      <div
        className="px-4 pt-3 pb-2 flex flex-col text-white font-semibold"
        style={{ backgroundColor: nodeColor, borderRadius: collapsed ? '0.5rem' : '0.5rem 0.5rem 0 0' }}
      >
        <div className="flex items-center gap-2">
          <BarChart3 size={16} className="flex-shrink-0" />
          <span className="truncate flex-1" title={displayName}>{displayName}</span>
          {!collapsed && <span className="text-xs opacity-80">FACT</span>}
          <button
            className="p-0.5 hover:bg-white/20 rounded transition-colors flex-shrink-0"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); toggleNodeCollapsed(id); }}
            title={collapsed ? 'Expand entity' : 'Collapse entity'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
        {domains.length > 0 && (
          <p className="text-xs italic font-normal text-white/75 mt-0.5 pl-6 truncate leading-tight">
            {domains.map(d => d.text).join(' · ')}
          </p>
        )}
      </div>

      {/* Body — hidden when collapsed */}
      {!collapsed && (
        <>
          {showFieldDescriptions && data.description && (
            <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800">
              <p className="text-xs text-amber-800 dark:text-amber-300 italic whitespace-pre-line">
                {wrapText(data.description)}
              </p>
            </div>
          )}

          <div className="p-2">
            {fields.length === 0 ? (
              <div className="text-gray-400 text-xs italic p-2">No fields</div>
            ) : (
              <>
                {dimensions.length > 0 && (
                  <div className="mb-2">
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 px-2">Dimensions</div>
                    <div className="space-y-1">
                      {dimensions.map((field, index) => {
                        const isPartitionKey = partitionColumns.includes(field.name);
                        return (
                          <div
                            key={index}
                            className="flex items-start justify-between text-xs p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                          >
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <div className="flex items-center gap-0.5 flex-shrink-0 mt-0.5">
                                {field.isPrimaryKey && <Key size={12} className="text-amber-500" />}
                                {field.autoIncrement && <Hash size={12} className="text-blue-500" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  <span
                                    className={`truncate ${
                                      isPartitionKey
                                        ? 'text-blue-600 dark:text-blue-400 font-semibold'
                                        : 'text-gray-900 dark:text-gray-100'
                                    } ${field.isPrimaryKey ? 'font-bold' : ''}`}
                                    title={isPartitionKey ? `Partition Key (${partitioning?.type?.toUpperCase()})` : undefined}
                                  >
                                    {field.name}
                                  </span>
                                  {isPartitionKey && (
                                    <span
                                      className="text-[9px] px-1 py-0.2 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-semibold rounded"
                                      title={`Partition Key (${partitioning?.type?.toUpperCase()})`}
                                    >
                                      PART
                                    </span>
                                  )}
                                  {field.nullable === false && (
                                    <span className="text-red-500 font-bold text-xs" title="NOT NULL">*</span>
                                  )}
                                </div>
                                {showFieldDescriptions && field.description && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400 italic block whitespace-pre-line">
                                    {wrapText(field.description)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs ml-2 flex-shrink-0">{field.type}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {virtualFKFields.length > 0 && (
                  <div className="mb-2">
                    <div className="space-y-1">
                      {virtualFKFields.map((fkField, index) => (
                        <div
                          key={`fk-${index}`}
                          className="flex items-start gap-2 text-xs p-2 rounded transition-colors bg-purple-50/50 dark:bg-purple-900/10"
                          title={`Foreign key to ${fkField.sourceName}`}
                        >
                          <Link2 size={12} className="text-purple-500 flex-shrink-0 mt-0.5" />
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <FkRelationshipAlias alias={fkField.fkAlias} />
                            <span className="truncate text-purple-700 dark:text-purple-400 italic font-medium">
                              {fkField.sourceName}
                            </span>
                            <span className="text-[10px] px-1 py-0.5 bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 rounded font-bold">FK</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {measures.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 px-2 flex items-center gap-1">
                      <TrendingUp size={12} />
                      Measures
                    </div>
                    <div className="space-y-1">
                      {measures.map((field, index) => (
                        <div
                          key={index}
                          className="flex items-start justify-between text-xs p-2 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded"
                        >
                          <div className="flex-1 min-w-0 mr-2">
                            <div className="flex items-center gap-1">
                              <span className="truncate font-medium text-gray-900 dark:text-gray-100">{field.name}</span>
                              {field.nullable === false && (
                                <span className="text-red-500 font-bold text-xs" title="NOT NULL">*</span>
                              )}
                            </div>
                            {showFieldDescriptions && field.description && (
                              <span className="text-xs text-gray-600 dark:text-gray-400 italic block whitespace-pre-line">
                                {wrapText(field.description)}
                              </span>
                            )}
                          </div>
                          <span className="text-gray-500 dark:text-gray-400 text-xs flex-shrink-0">{field.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Handles — always present */}
      {targetPositions.map((position, index) => (
        <Handle
          key={`target-${index}`}
          type="target"
          position={Position.Left}
          id={`target-${index}`}
          className="w-3 h-3"
          style={{ background: nodeColor, top: `${position}%` }}
        />
      ))}
      {sourcePositions.map((position, index) => (
        <Handle
          key={`source-${index}`}
          type="source"
          position={Position.Right}
          id={`source-${index}`}
          className="w-3 h-3"
          style={{ background: nodeColor, top: `${position}%` }}
        />
      ))}
    </div>
    </>
  );
};

export default memo(FactNode);
